/**
 * Renders predicted-trajectory (velocity-vector) lines on the map in both 2D
 * (google.maps.Polyline) and 3D (Polyline3DElement) modes. Mirrors the diffed
 * update/remove approach of track-history so lines stay subordinate to markers.
 */

import type { ViewMode, MapInstance } from '../map-setup';

/** A single trajectory line from a track's current position to its projection */
export interface TrajectorySegment {
  uid: string;
  from: google.maps.LatLngLiteral;
  to: google.maps.LatLngLiteral;
  color: string;
}

/** Stroke weight for the thin, marker-subordinate vector line */
const LINE_2D_WEIGHT = 2;
const LINE_3D_WIDTH = 2;

const polylines2d = new Map<string, google.maps.Polyline>();
const polylines3d = new Map<string, google.maps.maps3d.Polyline3DElement>();
let activeMap3d: google.maps.maps3d.Map3DElement | null = null;

/** Render/update the given trajectory segments for the active view mode. */
export function renderTrajectories(
  map: MapInstance,
  mode: ViewMode,
  segments: TrajectorySegment[],
): void {
  if (mode === '2d') {
    render2d(map as google.maps.Map, segments);
  } else {
    activeMap3d = map as google.maps.maps3d.Map3DElement;
    render3d(activeMap3d, segments);
  }
}

/** Remove all trajectory lines for the given mode. */
export function clearTrajectories(mode: ViewMode): void {
  if (mode === '2d') {
    for (const line of polylines2d.values()) line.setMap(null);
    polylines2d.clear();
    return;
  }
  for (const line of polylines3d.values()) removeChild3d(line);
  polylines3d.clear();
}

/** 2D: a thin line with a forward arrowhead at the projected end. */
function render2d(map: google.maps.Map, segments: TrajectorySegment[]): void {
  const seen = new Set<string>();
  for (const segment of segments) {
    seen.add(segment.uid);
    const path = [segment.from, segment.to];
    const existing = polylines2d.get(segment.uid);
    if (existing) {
      existing.setPath(path);
      existing.setOptions({ strokeColor: segment.color });
      continue;
    }
    polylines2d.set(segment.uid, new google.maps.Polyline({
      path,
      strokeColor: segment.color,
      strokeOpacity: 0.7,
      strokeWeight: LINE_2D_WEIGHT,
      icons: [{ icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW }, offset: '100%' }],
      map,
    }));
  }
  removeStale2d(seen);
}

/** 3D: a thin clamped line to the projected end. */
function render3d(map3d: google.maps.maps3d.Map3DElement, segments: TrajectorySegment[]): void {
  const seen = new Set<string>();
  for (const segment of segments) {
    seen.add(segment.uid);
    const coordinates = [
      { lat: segment.from.lat, lng: segment.from.lng, altitude: 0 },
      { lat: segment.to.lat, lng: segment.to.lng, altitude: 0 },
    ];
    const existing = polylines3d.get(segment.uid);
    if (existing) {
      existing.coordinates = coordinates;
      continue;
    }
    const line = new google.maps.maps3d.Polyline3DElement({
      coordinates,
      altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
      strokeColor: segment.color,
      strokeWidth: LINE_3D_WIDTH,
      drawsOccludedSegments: true,
    });
    polylines3d.set(segment.uid, line);
    map3d.appendChild(line);
  }
  removeStale3d(seen);
}

/** Remove 2D lines whose track is no longer projected. */
function removeStale2d(seen: Set<string>): void {
  for (const [uid, line] of polylines2d) {
    if (seen.has(uid)) continue;
    line.setMap(null);
    polylines2d.delete(uid);
  }
}

/** Remove 3D lines whose track is no longer projected. */
function removeStale3d(seen: Set<string>): void {
  for (const [uid, line] of polylines3d) {
    if (seen.has(uid)) continue;
    removeChild3d(line);
    polylines3d.delete(uid);
  }
}

/** Detach a 3D polyline, tolerating an already-removed element. */
function removeChild3d(line: google.maps.maps3d.Polyline3DElement): void {
  try {
    activeMap3d?.removeChild(line);
  } catch {
    /* already removed */
  }
}
